import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { cn } from '@/utils';
import { X } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';
type ToastOptions = {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
};
type ToastItem = ToastOptions & { id: string };

type ToastContextValue = {
  show: (options: ToastOptions) => void;
  remove: (id: string) => void;
  clear: () => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const remove = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  };

  const show = (options: ToastOptions) => {
    const id = generateId();
    const item: ToastItem = {
      id,
      duration: options.duration ?? 3000,
      variant: options.variant ?? 'info',
      title: options.title,
      description: options.description,
    };
    setToasts((prev) => [...prev, item]);
    const handle = window.setTimeout(() => remove(id), item.duration!);
    timers.current.set(id, handle);
  };

  const clear = () => {
    toasts.forEach((t) => remove(t.id));
  };

  const value = useMemo(() => ({ show, remove, clear }), [toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onClose={remove} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

function ToastViewport({ toasts, onClose }: { toasts: ToastItem[]; onClose: (id: string) => void }) {
  return (
    <div className="fixed top-4 right-4 z-[60] flex w-80 flex-col gap-3">
      {toasts.map((t) => (
        <ToastItemView key={t.id} item={t} onClose={() => onClose(t.id)} />
      ))}
    </div>
  );
}

function ToastItemView({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const variantStyles: Record<ToastVariant, string> = {
    success:
      'border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-900/60 dark:text-green-100',
    error:
      'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/60 dark:text-red-100',
    warning:
      'border-yellow-200 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-100',
    info:
      'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-900/60 dark:text-blue-100',
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border p-3 shadow-sm backdrop-blur',
        variantStyles[item.variant ?? 'info']
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex-1">
        {item.title && <div className="text-sm font-semibold">{item.title}</div>}
        {item.description && <div className="mt-0.5 text-xs opacity-90">{item.description}</div>}
      </div>
      <button
        type="button"
        aria-label="Close"
        className="rounded p-1 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        onClick={onClose}
      >
        <X size={16} />
      </button>
    </div>
  );
}